import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import {
  MessageItem,
  type AssistantMessage,
  type AssistantScope,
} from '@/modules/assistant';
import {
  useAssistantContext,
  useAssistantMessages,
  useCreateAssistantThread,
  useSendAssistantMessage,
  useSelectedAssistantThreadId,
  useSelectAssistantThread,
} from '@/modules/assistant';
import { useStreamingText } from './hooks/use-streaming-text';
import { StreamingMessage } from './streaming-message';

export type ChatPanelProps = {
  /** The route the person is on — selects the assistant skill and suggestions. */
  route: string;
  /** Which assistant family to ask — decided by the route. */
  scope: AssistantScope;
};

/**
 * The conversation panel: message log plus the composer pinned at the bottom.
 *
 * Reuses the assistant module's hooks and parts, but lays them out as a full
 * page instead of a drawer. Streaming is simulated client-side: when a turn
 * arrives, its text is revealed chunk by chunk with a caret at the end.
 *
 * The API returns the full answer in one POST, so we open the conversation
 * first (when there is none), send the turn, then reveal the answer text
 * progressively through `useStreamingText`.
 */
export function ChatPanel({ route, scope }: ChatPanelProps) {
  const [composerText, setComposerText] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  const context = useAssistantContext(scope, route);
  const threadId = useSelectedAssistantThreadId(scope);
  const messages = useAssistantMessages(scope, threadId);
  const selectThread = useSelectAssistantThread();
  const createThread = useCreateAssistantThread(scope);
  const sendMessage = useSendAssistantMessage(scope);

  const { revealed: streamRevealed } = useStreamingText({
    text: streamingText,
    onComplete: () => setIsStreaming(false),
  });

  const handleSend = async (text: string) => {
    const value = text.trim();
    if (value === '') return;
    setComposerText('');
    setStreamingText('');
    setIsStreaming(false);
    try {
      const target = threadId ?? (await createThread.mutateAsync({ route })).id;
      selectThread(target, scope);
      const turn = await sendMessage.mutateAsync({ threadId: target, message: value, route });
      setStreamingText(turn.message.content);
      setIsStreaming(true);
    } catch {
      // The failure is carried by the hooks and shown under the composer.
    }
  };

  const isPending = createThread.isPending || sendMessage.isPending;
  const agentName = context.data?.agent.name ?? 'Assistant';
  const disabled = composerText.trim() === '' || isStreaming || isPending;
  const loading = isStreaming || isPending;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface/50 px-3 py-2">
        <h2 className="font-semibold text-content">Conversation</h2>
        <Button size="sm" variant="ghost" disabled={threadId === null} onClick={() => selectThread(null, scope)}>
          New conversation
        </Button>
      </div>

      <MessageLog messages={messages} agentName={agentName} streamRevealed={streamRevealed} isStreaming={isStreaming} />

      <Composer composerText={composerText} setComposerText={setComposerText} disabled={disabled} loading={loading} onSend={handleSend} />
    </div>
  );
}

function MessageLog({
  messages,
  agentName,
  streamRevealed,
  isStreaming,
}: {
  messages: { data: AssistantMessage[] | undefined; isPending: boolean };
  agentName: string;
  streamRevealed: string;
  isStreaming: boolean;
}) {
  const resolvedMessages = messages.data ?? [];

  return (
    <div
      role="log"
      aria-live="polite"
      aria-label="Conversation"
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3"
    >
      {messages.isPending && (
        <p className="text-sm text-content-muted">Waiting for the assistant...</p>
      )}

      {resolvedMessages.length === 0 && !messages.isPending && !isStreaming ? (
        <p className="text-sm text-content-muted">This conversation has no messages yet.</p>
      ) : null}

      {resolvedMessages.map((message: AssistantMessage) => (
        <MessageItem
          key={message.id}
          message={message}
          agentName={agentName}
        />
      ))}

      {isStreaming ? (
        <StreamingMessage text={streamRevealed} isStreaming={true} ariaLabel="Assistant answer" />
      ) : null}
    </div>
  );
}

function Composer({
  composerText,
  setComposerText,
  disabled,
  loading,
  onSend,
}: {
  composerText: string;
  setComposerText: (text: string) => void;
  disabled: boolean;
  loading: boolean;
  onSend: (text: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-border bg-surface-raised p-3">
      <Textarea
        label="Message the assistant"
        hideLabel
        rows={2}
        value={composerText}
        placeholder="Ask about this page"
        onChange={(event) => setComposerText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.shiftKey) return;
          event.preventDefault();
          void onSend(composerText);
        }}
      />
      <div className="flex items-end justify-between gap-2">
        <Button size="sm" onClick={() => void onSend(composerText)} disabled={disabled} loading={loading}>
          Send
        </Button>
      </div>
    </div>
  );
}
